import React, { useMemo } from "react";
import { Box, Grid, Tab, Tabs, Typography } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { ErrorBoundary } from "@common/error-boundary";
import { makeStyles } from "@material-ui/core/styles";
import { TabName, TabProps } from "./utils";
import { ContractTab } from "./contract-tab/contract-tab";
import { EventLog } from "./event-log/event-log";
import { QualityChecksTab } from "./quality-checks";
import { FilesTab } from "./files";
import { ConsultantGdpr } from "./gdpr/consultant-gdpr";
import { WorkStatistics } from "./work-statistics/work-statistics";
import { ConsultantCv } from "./consultant-cv/consultant-cv";
import { FreshdeskTicketsTab } from "./freshdesk-tickets/freshdesk-tickets-tab";
import { FreshdeskTicketsTabLabel } from "./freshdesk-tickets/freshdesk-tickets-tab-label";
import { ServicePreferencesTab } from "./service-preferences";

type Props = {
  consultantId: string;
  isUnfit: boolean;
  personalInformation: React.ReactNode;
  switches: React.ReactNode;
  actionButtons: React.ReactNode;
  tab: TabName;
  setTab: (tab: TabName) => void;
};

export function ConsultantProfileLayout(props: Props) {
  const classes = useStyles(props);

  const freshdeskLabel = useMemo(
    () => (
      <ErrorBoundary>
        <FreshdeskTicketsTabLabel consultantId={props.consultantId} />
      </ErrorBoundary>
    ),
    [props.consultantId]
  );

  const tabs: TabProps[] = [
    {
      label: locale.ActionLog,
      value: "eventLog",
      content: () => <EventLog consultantId={props.consultantId} />
    },
    {
      label: locale.Files,
      value: "files",
      content: () => <FilesTab consultantId={props.consultantId} />
    },
    {
      label: locale.Contract,
      value: "contracts",
      content: () => <ContractTab consultantId={props.consultantId} />
    },
    {
      label: locale.GDPR,
      value: "gdpr",
      content: () => <ConsultantGdpr consultantId={props.consultantId} />
    },
    {
      label: locale.QualityRate,
      value: "quality",
      content: () => <QualityChecksTab consultantId={props.consultantId} />
    },
    {
      label: "CV",
      value: "cv",
      content: () => <ConsultantCv consultantId={props.consultantId} />
    },
    {
      label: locale.workStatistics,
      value: "work-statistics",
      content: (isActive: boolean) => (
        <WorkStatistics consultantId={props.consultantId} isActive={isActive} />
      )
    },
    {
      label: freshdeskLabel,
      value: "freshdesk-tickets",
      content: (isActive: boolean) => (
        <FreshdeskTicketsTab consultantId={props.consultantId} isActive={isActive} />
      )
    },
    {
      label: locale.servicePreferences.title,
      value: "service-preferences",
      content: () => <ServicePreferencesTab consultantId={props.consultantId} />
    }
  ];

  const activeTab = tabs.find((tab) => tab.value === props.tab);

  return (
    <div>
      {props.isUnfit && (
        <div className={classes.errorBanner}>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {locale.UnfitConsultantObs}
          </Typography>
        </div>
      )}

      <div className={classes.root} data-test="ConsultantPageLayout">
        <Grid container spacing={2}>
          <Grid
            container
            item
            xs={12}
            md={3}
            lg={3}
            spacing={2}
            alignContent="flex-start"
          >
            <Grid item xs={12}>
              {props.personalInformation}
            </Grid>
            <Grid item xs={12}>
              {props.switches}
            </Grid>
          </Grid>
          <Grid container item xs={12} md={9} lg={9}>
            <Grid item xs={12}>
              <Box style={{ display: "flex", flexDirection: "column", rowGap: "1rem" }}>
                {props.actionButtons}
                <Tabs value={props.tab}>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                      onClick={() => props.setTab(tab.value)}
                    />
                  ))}
                </Tabs>
                {activeTab && <ErrorBoundary>{activeTab.content(true)}</ErrorBoundary>}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: ThemeType) => {
  return {
    root: {
      padding: theme.spacing(2),
      flexGrow: 1
    },
    errorBanner: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      width: "100%",
      padding: "1rem 2rem"
    }
  };
});

export default ConsultantProfileLayout;
